import React from "react";
import "setimmediate";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import {
  CardStyleInterpolators,
  createStackNavigator,
} from "@react-navigation/stack";
import {
  AppStateController,
  AuthProvider,
  FlashMessagesProvider,
  MeGuard,
  ModalCloseButton,
  OnlineStatusNotifier,
  useWithAppState,
} from "@towersystems/roam-common-native";
import "setimmediate";
import { useFonts } from "expo-font";
import * as SplashScreen from "expo-splash-screen";
import { useCallback, useEffect, useState } from "react";
import { Colors, View } from "react-native-ui-lib";
import useCachedResources from "../../../hooks/useCachedResources";
import { LogInScreen } from "../../modules/login";
import { SaleSyncController } from "../../modules/point-of-sale/components/SaleSyncController";
import { DataErrorBoundary } from "../DataErrorBoundry";
import { GlobalSettingsController } from "../GlobalSettingsController";
import { PrefetchGuard } from "../PrefetchGuard";
import {
  CameraPermission,
  PermissionsManager,
} from "../../utilities/permissions";
import {
  APP_STATE_KEY,
  defaultAppState,
} from "../../utilities/app-state/constants";
import { StreamProvider } from "../StreamProvider";
import { BarcodeSettingsProvider } from "../../modules/barcode-scanner/components";
import { VendorController } from "../VendorController";
import { useAuthentication } from "@towersystems/roam-common-native/lib/utilities/authentication";
import { gql, useMutation } from "@apollo/client";

const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout {
      success
    }
  }
`;
import { useDatabase } from "@nozbe/watermelondb/hooks";
import * as SecureStore from "@towersystems/roam-common-native";
import { LinklyPosProvider } from "../../utilities/linkly/useLinklyPosContext";
import { useLinklyPos } from "../../utilities/linkly/useLinklyPos";
import packageInfo from "../../../package.json";
import { noopApiEventListener } from "../../utilities/linkly/constants";
import {
  LinklyPowerRecoveryContextProvider,
  useLinklyPowerRecovery,
} from "../../utilities/linkly/useLinklyPowerRecovery";
import { StockCountSyncController as StockOrderCountSyncController } from "../../modules/stock-counts/components";
import { AuthorizedAppRootScreenParamList } from "../../types";
import { AppRootScreen } from "../../modules/app/screens/AppRootScreen";
import { SetupRootScreen } from "../../modules/setup/screens";
import { SwitchUserRootScreen } from "../../modules/point-of-sale/screens/SwitchUserRootScreen";
import { OnboardingRootScreen } from "../../modules/onboarding";
import { EurekaOAuthCallbackScreen } from "../../modules/login/screens/EurekaOAuthCallbackScreen";

const Stack = createNativeStackNavigator();

const AuthorizedStack =
  createStackNavigator<AuthorizedAppRootScreenParamList>();

const AuthenticatedApp = () => {
  const database = useDatabase();
  const { clearAppState } = useWithAppState();

  const handleOnTenantChange = useCallback(async () => {
    // reset the database
    await database.write(async () => {
      await database.unsafeResetDatabase().catch((e) => console.log(e));
    });
    await clearAppState();
  }, [database, clearAppState]);

  const linkly = useLinklyPos({
    config: {
      vendorDetails: {
        name: "Tower Systems",
        version: packageInfo.version,
        posId: "3e253580-601c-46ac-87ef-3614a921bfc1",
        posVendorId: "5aeccd55-5075-48ae-940d-9b361da14bf9",
      },
      apiServiceEndPoints: {
        posApiBaseUri: "https://rest.pos.cloud.pceftpos.com",
        AuthApiBaseUri: "https://auth.cloud.pceftpos.com",
      },
      eventListener: noopApiEventListener,
    },
  });

  const linklyPowerRecovery = useLinklyPowerRecovery();

  const [logoutMutation] = useMutation(LOGOUT_MUTATION);
  const authentication = useAuthentication({
    onDifferentTenant: handleOnTenantChange,
    onLogout: () => { logoutMutation().catch(() => {}); },
  });

  const { bootstraped, bootstrapAsync } = authentication;

  useEffect(() => {
    if (false === bootstraped) {
      bootstrapAsync();
    }
  }, [bootstraped]);

  if (!bootstraped) {
    return null;
  }

  return (
    <AuthProvider authentication={authentication}>
      {!authentication.isLoggedIn ? (
        <Stack.Navigator
          screenOptions={{
            headerShown: false,
            contentStyle: { backgroundColor: Colors.surface },
          }}
        >
          <Stack.Screen name="LogInScreen" component={LogInScreen} />
          <Stack.Screen
            name="EurekaOAuthCallbackScreen"
            component={EurekaOAuthCallbackScreen}
          />
        </Stack.Navigator>
      ) : (
        <DataErrorBoundary>
          <MeGuard>
            <VendorController>
              <GlobalSettingsController>
                <PrefetchGuard>
                  <BarcodeSettingsProvider>
                    <StreamProvider>
                      <LinklyPosProvider {...linkly}>
                        <LinklyPowerRecoveryContextProvider
                          {...linklyPowerRecovery}
                        >
                          <SaleSyncController>
                            <StockOrderCountSyncController>
                              <AuthorizedStack.Navigator
                                screenOptions={{
                                  headerShown: false,
                                  animationEnabled: true,
                                }}
                              >
                                <AuthorizedStack.Screen
                                  name="SetupRootScreen"
                                  component={SetupRootScreen}
                                />
                                <AuthorizedStack.Screen
                                  name="OnboardingRootScreen"
                                  component={OnboardingRootScreen}
                                  options={{
                                    animationEnabled: true,
                                  }}
                                />
                                <AuthorizedStack.Screen
                                  name="AppRootScreen"
                                  component={AppRootScreen}
                                  options={{
                                    animationEnabled: false,
                                  }}
                                />
                                <AuthorizedStack.Group
                                  screenOptions={{
                                    presentation: "modal",
                                    headerLeft: () => <ModalCloseButton />,
                                    animationEnabled: true,
                                    cardStyleInterpolator:
                                      CardStyleInterpolators.forVerticalIOS,
                                    headerShown: false,
                                    cardStyle: {
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                >
                                  <AuthorizedStack.Screen
                                    name="SwitchUserRootScreen"
                                    component={SwitchUserRootScreen}
                                  />
                                </AuthorizedStack.Group>
                              </AuthorizedStack.Navigator>
                            </StockOrderCountSyncController>
                          </SaleSyncController>
                        </LinklyPowerRecoveryContextProvider>
                      </LinklyPosProvider>
                    </StreamProvider>
                  </BarcodeSettingsProvider>
                </PrefetchGuard>
              </GlobalSettingsController>
            </VendorController>
          </MeGuard>
        </DataErrorBoundary>
      )}
    </AuthProvider>
  );
};
export function App() {
  const isLoadingComplete = useCachedResources();

  const [, setPermissionsInitialised] = useState(false);

  const initPermissions = async () => {
    try {
      await PermissionsManager.registerManagedPermissions([
        new CameraPermission(),
      ]);

      setPermissionsInitialised(true);
    } catch (err) {
      console.log(err);
      console.log("failed to initialise permissions");
      setPermissionsInitialised(true);
    }
  };

  useEffect(() => {
    initPermissions().catch((err) => console.log(err));
  }, []);

  const [fontsLoaded] = useFonts({
    Roboto: require("../../../assets/fonts/Roboto-Regular.ttf"),
    "Roboto-Bold": require("../../../assets/fonts/Roboto-Bold.ttf"),
    "Roboto-Medium": require("../../../assets/fonts/Roboto-Medium.ttf"),
    "Roboto-Light": require("../../../assets/fonts/Roboto-Light.ttf"),
    "Gilmer-Regular": require("../../../assets/fonts/Gilmer-Regular.ttf"),
    "Gilmer-Bold": require("../../../assets/fonts/Gilmer-Bold.ttf"),
    "Gilmer-Heavy": require("../../../assets/fonts/Gilmer-Heavy.ttf"),
    "Gilmer-Light": require("../../../assets/fonts/Gilmer-Light.ttf"),
    "Gilmer-Medium": require("../../../assets/fonts/Gilmer-Medium.ttf"),
    "Gilmer-Outline": require("../../../assets/fonts/Gilmer-Outline.ttf"),
  });

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded) {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);

  if (!isLoadingComplete) {
    return null;
  }

  return (
    <AppStateController stateKey={APP_STATE_KEY} defaultState={defaultAppState}>
      <View
        flex
        onLayout={onLayoutRootView}
        style={{ backgroundColor: Colors.surface }}
      >
        <FlashMessagesProvider>
          <OnlineStatusNotifier />
          <AuthenticatedApp />
        </FlashMessagesProvider>
      </View>
    </AppStateController>
  );
}
